import React, { useState, useEffect } from 'react';
import { Select } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';

enum DurationOption {
  DEFAULT = '',
  TODAY = 'TODAY',
  LAST_7_DAYS = 'last_7_days',
  CURRENT_MONTH = 'current_month',
  LAST_MONTH = 'last_month',
  CUSTOM_RANGE = 'custom_range',
}

interface DurationData {
  startDate: string;
  endDate: string;
}

interface DurationSelectorProps {
  onDateChange: (start: string, end: string) => void;
}

const DurationSelector: React.FC<DurationSelectorProps> = ({ onDateChange }) => {
  const [selectedDuration, setSelectedDuration] = useState<DurationOption>(DurationOption.DEFAULT);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  const handleDurationChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedDuration(event.target.value as DurationOption);
  };

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.name === 'start_date') {
      setStartDate(event.target.value);
    } else if (event.target.name === 'end_date') {
      setEndDate(event.target.value);
    }
  };

  const getDurationData = (): DurationData => {
    if (selectedDuration === DurationOption.DEFAULT) {
      return { startDate: '', endDate: '' };
    }

    const today = new Date();
    let start = new Date();
    let end = new Date();

    switch (selectedDuration) {
      case DurationOption.TODAY:
        break;
      case DurationOption.LAST_7_DAYS:
        start.setDate(today.getDate() - 7);
        break;
      case DurationOption.CURRENT_MONTH:
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      case DurationOption.LAST_MONTH:
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case DurationOption.CUSTOM_RANGE:
        return { startDate, endDate };
      default:
        break;
    }

    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0],
    };
  };

  useEffect(() => {
    const { startDate, endDate } = getDurationData();
    onDateChange(startDate, endDate);
  }, [selectedDuration, startDate, endDate]);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <div className="flex items-center gap-1">
          <Label htmlFor="jobsiteduration">Select Report Duration:</Label>
          <span className="text-red-600 font-semibold">*</span>
        </div>
        <Select
          id="jobsiteduration"
          value={selectedDuration}
          onChange={handleDurationChange}
          required
        >
          <option value="">Select Duration</option>
          <option value={DurationOption.TODAY}>Today</option>
          <option value={DurationOption.LAST_7_DAYS}>Last 7 Days</option>
          <option value={DurationOption.CURRENT_MONTH}>Current Month</option>
          <option value={DurationOption.LAST_MONTH}>Last Month</option>
          <option value={DurationOption.CUSTOM_RANGE}>Custom Range</option>
        </Select>
      </div>

      {selectedDuration === DurationOption.CUSTOM_RANGE && (
        <div className="space-y-4 pl-4 border-l-2 border-gray-200">
          <div className="space-y-2">
            <Label htmlFor="start_date">Start Date:</Label>
            <Input
              type="date"
              id="start_date"
              name="start_date"
              value={startDate}
              onChange={handleDateChange}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="end_date">End Date:</Label>
            <Input
              type="date"
              id="end_date"
              name="end_date"
              value={endDate}
              onChange={handleDateChange}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DurationSelector;